import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Bell, ChefHat, UtensilsCrossed, CheckCircle2, Clock, XCircle,
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Package, TrendingUp,
  BedDouble, RefreshCw, Search, X, AlertCircle, History, Monitor,
  Image, Timer, Star, BarChart3,
  Truck, MessageSquare, AlertTriangle, Check, User, LayoutGrid, Trash,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import NewOrderFlow from "./NewOrderFlow";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./RoomServiceDashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

/* ── Types ── */
interface OrderItem { name: string; qty: number; price: number; notes?: string }
interface Order {
  id: number; room_number: string; guest_name?: string;
  items: OrderItem[]; status: "pending" | "preparing" | "on_the_way" | "delivered" | "cancelled";
  total: number; notes?: string; created_at: string;
  is_room?: boolean; has_booking?: boolean; booking_guest?: string | null;
  room_status?: string; billed?: boolean; phone_number?: number | string;
}
interface MenuItem {
  id: number; name: string; category: string; price: number;
  description?: string; available: boolean; ingredients: string[];
  unit: string; image_url?: string; available_from?: string; available_to?: string;
}
interface Stats {
  total: number; pending: number; preparing: number; on_the_way: number; delivered: number; cancelled: number; revenue: number;
}
interface Analytics {
  topItems: { name: string; count: number; revenue: number }[];
  revenueByCategory: { category: string; revenue: number }[];
  peakHours: { hour: number; count: number }[];
  avgDeliveryTime: number | null;
  cancellationRate: number;
  dailyRevenue: { date: string; revenue: number }[];
  totalRevenue7d: number;
  totalOrders7d: number;
}

const STATUS_META = {
  pending:    { label: "Pending",     color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  icon: Clock },
  preparing:  { label: "Preparing",   color: "#6366f1", bg: "rgba(99,102,241,0.12)",  icon: ChefHat },
  on_the_way: { label: "On the way",  color: "#38bdf8", bg: "rgba(56,189,248,0.12)",  icon: Truck },
  delivered:  { label: "Delivered",   color: "#34d399", bg: "rgba(52,211,153,0.12)",  icon: CheckCircle2 },
  cancelled:  { label: "Cancelled",   color: "#f87171", bg: "rgba(239,68,68,0.1)",    icon: XCircle },
};
const CATEGORIES = ["Breakfast", "Main", "Snacks", "Drinks", "Desserts", "Other"];

function elapsed(iso: string) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now"; if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
}
function fmt(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

function isAvailableNow(item: MenuItem): boolean {
  if (!item.available_from || !item.available_to) return true;
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const [fh, fm] = item.available_from.split(":").map(Number);
  const [th, tm] = item.available_to.split(":").map(Number);
  const fromMins = fh * 60 + fm, toMins = th * 60 + tm;
  if (fromMins <= toMins) return nowMins >= fromMins && nowMins <= toMins;
  return nowMins >= fromMins || nowMins <= toMins;
}

/* ── Live Timer ── */
function LiveTimer({ createdAt, status }: { createdAt: string; status: string }) {
  const [secs, setSecs] = useState(() => Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
  useEffect(() => {
    const iv = setInterval(() => setSecs(Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [createdAt]);
  const m = Math.floor(secs / 60), s = secs % 60;
  const danger = (status === "pending" && m >= 10) || (status === "preparing" && m >= 25);
  const warn   = (status === "pending" && m >= 5)  || (status === "preparing" && m >= 15);
  return (
    <span className={`rs-live-timer ${danger ? "danger" : warn ? "warn" : ""}`}>
      <Timer size={11} />{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

export default function RoomServiceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = (searchParams.get("tab") || "orders") as "orders" | "new-order" | "menu" | "history" | "analytics" | "tables" | "board" | "zreport";
  const setTab = (t: string) => setSearchParams({ tab: t });

  const [role, setRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [scope, setScope] = useState<"all" | "rooms" | "tables">("all"); // rooms vs restaurant tables
  const [autoNotify, setAutoNotify] = useState(true); // WhatsApp the guest on status changes
  const [toast, setToast] = useState<{ text: string; kind: "ok" | "err" } | null>(null);
  const showToast = (text: string, kind: "ok" | "err" = "ok") => { setToast({ text, kind }); setTimeout(() => setToast(null), 3200); };
  const [roomOrders, setRoomOrders] = useState<{ room: string; orders: Order[] } | null>(null); // per-room running tab
  const [acceptTarget, setAcceptTarget] = useState<Order | null>(null); // ETA prompt on accept
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);   // order details drawer
  const [zreport, setZreport] = useState<any>(null);
  const [zdate, setZdate] = useState(todayISO());
  const [orders, setOrders]   = useState<Order[]>([]);
  const [menu, setMenu]       = useState<MenuItem[]>([]);
  const [stats, setStats]     = useState<Stats>({ total: 0, pending: 0, preparing: 0, on_the_way: 0, delivered: 0, cancelled: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [menuSearch, setMenuSearch] = useState("");
  const [menuCat, setMenuCat] = useState("All");
  const [newOrderRing, setNewOrderRing] = useState(false);
  const prevOrderIds = useRef<Set<number>>(new Set());

  // History tab
  const [historyDate, setHistoryDate]     = useState(todayISO());
  const [historySearch, setHistorySearch] = useState("");
  const [history, setHistory]             = useState<Order[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Analytics tab
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  const [editingItem, setEditingItem]   = useState<Partial<MenuItem> | null>(null);
  const [editMode, setEditMode]         = useState<"create" | "edit">("create");
  const [ingredientInput, setIngredientInput] = useState("");

  const [showNewOrder, setShowNewOrder] = useState(false);
  const [newOrder, setNewOrder] = useState({ room_number: "", guest_name: "", notes: "", items: [] as OrderItem[], bookingId: "" });
  const [orderItemInput, setOrderItemInput] = useState({ name: "", qty: 1, price: 0, notes: "" });
  const [orderError, setOrderError]     = useState("");
  const [menuItemError, setMenuItemError] = useState("");
  const [inHouseMap, setInHouseMap]     = useState<Record<string, { guestName: string; bookingId: string }>>({});
  const [guestHistory, setGuestHistory] = useState<Order[]>([]);

  // Edit-order modal
  const [editOrder, setEditOrder]       = useState<Order | null>(null);
  const [editItems, setEditItems]       = useState<OrderItem[]>([]);
  const [editItemInput, setEditItemInput] = useState({ name: "", qty: 1, price: 0, notes: "" });
  const [editError, setEditError]       = useState("");
  const openEdit = (o: Order) => { setEditOrder(o); setEditItems(o.items.map(i => ({ ...i }))); setEditError(""); };

  // Per-table bill / settle
  const [billTable, setBillTable] = useState<{ table: string; orders: Order[] } | null>(null);
  const [billMethod, setBillMethod] = useState<"cash" | "card" | "instapay">("cash");
  const [settling, setSettling] = useState(false);
  const openBill = (table: string) => {
    const os = orders.filter(o => String(o.room_number) === String(table) && (o.status === "pending" || o.status === "preparing"));
    setBillTable({ table, orders: os }); setBillMethod("cash");
  };
  const settleTable = async () => {
    if (!billTable) return;
    setSettling(true);
    try {
      await axios.post(`${API}/api/roomservice/settle-table`, { table: billTable.table, method: billMethod }, { headers });
      setBillTable(null); loadOrders(); loadStats();
      showToast(`Table ${billTable.table} settled · ${billMethod}`);
    } finally { setSettling(false); }
  };
  const saveEdit = async () => {
    if (!editOrder) return;
    if (editItems.length === 0) { setEditError("An order needs at least one item."); return; }
    setEditError("");
    await axios.put(`${API}/api/roomservice/orders/${editOrder.id}`, { items: editItems }, { headers });
    setEditOrder(null);
    loadOrders(); loadStats();
  };

  // Audio
  const audioCtxRef = useRef<AudioContext | null>(null);

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Decode role
  useEffect(() => {
    if (!token) return;
    try { setRole((jwtDecode(token) as any).role || ""); } catch {}
  }, []);

  // Init audio context on first click
  useEffect(() => {
    const init = () => {
      if (!audioCtxRef.current)
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      document.removeEventListener("click", init);
    };
    document.addEventListener("click", init);
    return () => document.removeEventListener("click", init);
  }, []);

  // Request push notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default")
      Notification.requestPermission();
  }, []);

  function playDing() {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.9);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.9);
    });
  }

  function notifyNewOrder(order: Order) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(`[Order] New Order — Room ${order.room_number}`, {
        body: `${order.items?.length || 0} items · EGP ${order.total}`,
        tag: `rs-order-${order.id}`,
      });
    }
  }

  const loadOrders = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roomservice/orders`, { headers });
    const fresh: Order[] = data || [];
    const freshIds = new Set(fresh.map(o => o.id));
    const newOnes = fresh.filter(o => !prevOrderIds.current.has(o.id));
    if (prevOrderIds.current.size > 0 && newOnes.length > 0) {
      setNewOrderRing(true);
      setTimeout(() => setNewOrderRing(false), 3000);
      playDing();
      newOnes.forEach(notifyNewOrder);
    }
    prevOrderIds.current = freshIds;
    setOrders(fresh);
  }, []);

  const loadMenu  = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roomservice/menu`, { headers });
    setMenu(data || []);
  }, []);

  const loadStats = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roomservice/stats`, { headers });
    setStats(data);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    // Unblock the page as soon as orders (the primary content) arrive;
    // menu + stats aren't needed to render order cards, so load them in the background.
    await loadOrders();
    setLoading(false);
    Promise.all([loadMenu(), loadStats()]).catch(() => {});
  }, [loadOrders, loadMenu, loadStats]);

  useEffect(() => { loadAll(); const iv = setInterval(() => { loadOrders(); loadStats(); }, 15000); return () => clearInterval(iv); }, []);

  // History tab load
  useEffect(() => {
    if (tab !== "history") return;
    setHistoryLoading(true);
    axios.get(`${API}/api/roomservice/orders?date=${historyDate}`, { headers })
      .then(({ data }) => setHistory(data || []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [tab, historyDate]);

  // Analytics tab load
  useEffect(() => {
    if (tab !== "analytics") return;
    axios.get(`${API}/api/roomservice/analytics`, { headers })
      .then(({ data }) => setAnalytics(data))
      .catch(() => {});
  }, [tab]);

  // Z-Report load
  useEffect(() => { if (tab === "zreport") loadZReport(zdate); }, [tab, zdate]);

  // In-house map when new order opens
  useEffect(() => {
    if (!showNewOrder) return;
    axios.get(`${API}/api/dashboard/inhouse`, { headers })
      .then(({ data }) => {
        const map: Record<string, { guestName: string; bookingId: string }> = {};
        (data || []).forEach((g: any) => {
          if (g.roomNumber) map[String(g.roomNumber)] = { guestName: g.guestName || "", bookingId: g.bookingId || g.id || "" };
        });
        setInHouseMap(map);
      }).catch(() => {});
  }, [showNewOrder]);

  // Guest history when room selected
  useEffect(() => {
    if (!newOrder.room_number) { setGuestHistory([]); return; }
    axios.get(`${API}/api/roomservice/guest-history?room=${newOrder.room_number}`, { headers })
      .then(({ data }) => setGuestHistory(data || []))
      .catch(() => {});
  }, [newOrder.room_number]);

  /* ── Order actions ── */
  const STATUS_TOAST: Record<string, string> = {
    preparing: "Order accepted — kitchen notified", on_the_way: "Marked on the way",
    delivered: "Marked delivered", cancelled: "Order rejected",
  };
  const updateStatus = async (id: number, status: string, notify = autoNotify, eta: number | null = null) => {
    try {
      await axios.put(`${API}/api/roomservice/orders/${id}/status`, { status, notify, eta }, { headers });
      loadOrders(); loadStats();
      showToast((STATUS_TOAST[status] || "Updated") + (notify ? " · guest messaged 💬" : ""));
    } catch { showToast("Couldn't update the order", "err"); }
  };
  const acceptOrder = (o: Order) => setAcceptTarget(o); // opens the ETA prompt
  const confirmAccept = (eta: number | null) => {
    if (!acceptTarget) return;
    updateStatus(acceptTarget.id, "preparing", autoNotify, eta);
    setAcceptTarget(null);
  };
  const loadZReport = useCallback(async (d: string) => {
    setZreport(null);
    try {
      const { data } = await axios.get(`${API}/api/roomservice/zreport?date=${d}`, { headers });
      setZreport(data);
    } catch { setZreport({ error: true }); }
  }, []);
  const rejectOrder = async (o: Order) => {
    if (!confirm(`Reject order for Room ${o.room_number}?`)) return;
    updateStatus(o.id, "cancelled", false);
  };
  const notifyOrder = async (o: Order, status: string) => {
    try {
      await axios.post(`${API}/api/roomservice/orders/${o.id}/notify`, { status }, { headers });
      showToast("Guest messaged on WhatsApp 💬");
    } catch (e: any) {
      showToast(e?.response?.data?.message || "WhatsApp send failed", "err");
    }
  };
  const cleanStale = async () => {
    if (!confirm("Cancel all pending orders older than 24 hours?")) return;
    try {
      const { data } = await axios.post(`${API}/api/roomservice/clean-stale`, { hours: 24 }, { headers });
      loadOrders(); loadStats();
      showToast(`Cleared ${data.cancelled} stale order${data.cancelled !== 1 ? "s" : ""}`);
    } catch { showToast("Couldn't clear stale orders", "err"); }
  };
  const openRoomTab = async (room: string) => {
    setRoomOrders({ room, orders: [] });
    try {
      const { data } = await axios.get(`${API}/api/roomservice/orders?room=${encodeURIComponent(room)}`, { headers });
      setRoomOrders({ room, orders: data || [] });
    } catch { setRoomOrders({ room, orders: [] }); }
  };
  const deleteOrder = async (id: number) => {
    if (!confirm("Delete this order?")) return;
    await axios.delete(`${API}/api/roomservice/orders/${id}`, { headers });
    loadOrders(); loadStats();
    showToast("Order deleted");
  };
  // Menu matching — does this ordered item exist on the menu? (bot writes free text)
  const menuMatch = (name: string) => menu.find(m => m.name.toLowerCase().trim() === (name || "").toLowerCase().trim());
  const submitNewOrder = async () => {
    if (!newOrder.room_number.trim()) { setOrderError("Room number is required."); return; }
    if (newOrder.items.length === 0) { setOrderError("Please add at least one item."); return; }
    setOrderError("");
    const total = newOrder.items.reduce((s, i) => s + i.price * i.qty, 0);
    await axios.post(`${API}/api/roomservice/orders`, { ...newOrder, total }, { headers });
    setShowNewOrder(false);
    setNewOrder({ room_number: "", guest_name: "", notes: "", items: [], bookingId: "" });
    loadOrders(); loadStats();
  };

  /* ── Menu actions ── */
  const saveMenuItem = async () => {
    if (!editingItem?.name?.trim()) { setMenuItemError("Item name is required."); return; }
    setMenuItemError("");
    if (editMode === "create") await axios.post(`${API}/api/roomservice/menu`, editingItem, { headers });
    else await axios.put(`${API}/api/roomservice/menu/${editingItem.id}`, editingItem, { headers });
    setEditingItem(null); loadMenu();
  };
  const toggleAvailable = async (item: MenuItem) => {
    await axios.put(`${API}/api/roomservice/menu/${item.id}`, { available: !item.available }, { headers });
    loadMenu();
  };
  const deleteMenuItem = async (id: number) => {
    if (!confirm("Delete this menu item?")) return;
    await axios.delete(`${API}/api/roomservice/menu/${id}`, { headers });
    loadMenu();
  };

  /* ── Derived ── */
  const inScope = (o: Order) => scope === "all" || (scope === "rooms" ? o.is_room !== false : o.is_room === false);
  const filteredOrders = orders.filter(o => {
    if (!inScope(o)) return false;
    if (filterStatus === "active") return o.status === "pending" || o.status === "preparing" || o.status === "on_the_way";
    if (filterStatus === "all") return true;
    return o.status === filterStatus;
  });

  // Restaurant tables (table_number that isn't a room) with their open orders, grouped for the Tables view
  const tableGroups = (() => {
    const map: Record<string, Order[]> = {};
    orders.filter(o => o.is_room === false && (o.status === "pending" || o.status === "preparing"))
      .forEach(o => { (map[o.room_number] ||= []).push(o); });
    return Object.entries(map)
      .map(([table, os]) => ({
        table,
        orders: os,
        total: os.reduce((s, o) => s + Number(o.total || 0), 0),
        count: os.length,
        oldest: os.reduce((min, o) => Math.min(min, new Date(o.created_at).getTime()), Date.now()),
        hasPending: os.some(o => o.status === "pending"),
      }))
      .sort((a, b) => Number(a.table) - Number(b.table) || a.table.localeCompare(b.table));
  })();
  const activeRooms = [...new Set(orders.filter(o => o.status === "pending" || o.status === "preparing").map(o => o.room_number))].sort();
  const filteredMenu = menu.filter(m => {
    const matchCat = menuCat === "All" || m.category === menuCat;
    const matchQ   = !menuSearch || m.name.toLowerCase().includes(menuSearch.toLowerCase());
    return matchCat && matchQ;
  });
  const filteredHistory = history.filter(o =>
    !historySearch || o.room_number.includes(historySearch) ||
    o.guest_name?.toLowerCase().includes(historySearch.toLowerCase()) ||
    o.items?.some(i => i.name.toLowerCase().includes(historySearch.toLowerCase()))
  );
  const isManager = false; // analytics moved to RoomServiceManagerDashboard

  const tabDefs = [
    { key: "orders",    label: "Orders",    icon: Package,     badge: stats.pending },
    { key: "board",     label: "Board",     icon: LayoutGrid,  badge: 0 },
    { key: "tables",    label: "Tables",    icon: UtensilsCrossed, badge: tableGroups.length },
    { key: "new-order", label: "New Order", icon: Plus,        badge: 0 },
    { key: "menu",      label: "Menu",      icon: UtensilsCrossed, badge: 0 },
    { key: "history",   label: "History",   icon: History,     badge: 0 },
    { key: "zreport",   label: "Z-Report",  icon: BarChart3,   badge: 0 },
    ...(isManager ? [{ key: "analytics", label: "Analytics", icon: BarChart3, badge: 0 }] : []),
  ];

  /* ── Reusable order card (used by the list grid and the board) ── */
  const renderOrderCard = (order: Order) => {
    const sm = STATUS_META[order.status] || STATUS_META.pending;
    const Icon = sm.icon;
    const active = order.status === "pending" || order.status === "preparing" || order.status === "on_the_way";
    const isUrgent = order.status === "pending" && (Date.now() - new Date(order.created_at).getTime()) > 10 * 60000;
    const isTable = order.is_room === false;
    const noBooking = order.is_room !== false && order.has_booking === false;
    const guest = order.booking_guest || order.guest_name;
    return (
      <div key={order.id} className={`rs-order-card ${isUrgent ? "urgent" : ""} ${noBooking ? "warn" : ""}`} style={{ borderColor: sm.color + "33" }}>
        <div className="rs-order-header">
          <div className="rs-order-room" onClick={() => !isTable && openRoomTab(order.room_number)} style={{ cursor: isTable ? "default" : "pointer" }} title={isTable ? "" : "View this room's orders"}>
            {isTable ? <UtensilsCrossed size={14} /> : <BedDouble size={14} />}
            <span>{isTable ? "Table" : "Room"} {order.room_number}</span>
            {isUrgent && <span className="rs-urgent-tag"><AlertCircle size={11} /> 10m+</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {active && <LiveTimer createdAt={order.created_at} status={order.status} />}
            <span className="rs-order-status-badge" style={{ background: sm.bg, color: sm.color }}>
              <Icon size={11} /> {sm.label}
            </span>
          </div>
        </div>

        <div className="rs-order-guestline">
          {guest && <span className="rs-order-guest"><User size={11} /> {guest}</span>}
          {order.phone_number && <span className="rs-badge wa">💬 WhatsApp</span>}
          {!isTable && (order.billed
            ? <span className="rs-badge billed"><Check size={10} /> On folio</span>
            : noBooking && <span className="rs-badge nobook"><AlertTriangle size={10} /> No active booking</span>)}
        </div>

        <div className="rs-order-time"><Clock size={11} /> {fmt(order.created_at)} · {elapsed(order.created_at)}</div>
        <div className="rs-order-items">
          {(order.items || []).map((item, i) => {
            const m = menuMatch(item.name);
            return (
              <div key={i} className={`rs-order-item ${!m ? "unmatched" : ""}`}>
                <span className="rs-item-qty">{item.qty}×</span>
                <span className="rs-item-name">{item.name}{!m && <AlertTriangle size={10} className="rs-item-flag" />}</span>
                <span className="rs-item-price">EGP {(item.price * item.qty).toLocaleString()}</span>
              </div>
            );
          })}
          {order.notes && <div className="rs-order-notes"><FontAwesomeIcon icon={faNoteSticky} /> {order.notes}</div>}
        </div>

        <div className="rs-order-footer">
          <span className="rs-order-total">EGP {Number(order.total).toLocaleString()}</span>
          <div className="rs-order-actions">
            {order.status === "pending" && (
              <>
                <button className="rs-action-btn preparing" onClick={() => acceptOrder(order)}><Check size={13} /> Accept</button>
                <button className="rs-action-btn cancel" onClick={() => rejectOrder(order)} title="Reject"><XCircle size={13} /></button>
              </>
            )}
            {order.status === "preparing" && <button className="rs-action-btn otw" onClick={() => updateStatus(order.id, "on_the_way")}><Truck size={13} /> On the way</button>}
            {order.status === "on_the_way" && <button className="rs-action-btn delivered" onClick={() => updateStatus(order.id, "delivered")}><CheckCircle2 size={13} /> Delivered</button>}
            {!!order.phone_number && (order.status === "preparing" || order.status === "on_the_way") && (
              <button className="rs-action-btn notify" onClick={() => notifyOrder(order, order.status)} title="Message guest on WhatsApp"><MessageSquare size={13} /></button>
            )}
            <button className="rs-action-btn info" onClick={() => setDetailOrder(order)} title="Details"><AlertCircle size={13} /></button>
            <button className="rs-action-btn edit" onClick={() => openEdit(order)} title="Edit items"><Pencil size={13} /></button>
            <button className="rs-action-btn delete" onClick={() => deleteOrder(order.id)}><Trash2 size={13} /></button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="rs-page">

        {/* ── Header ── */}
        <div className="rs-header">
          <div className="rs-header-left">
            <h1 className="rs-title">Room Service</h1>
            <p className="rs-subtitle">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <div className="rs-header-right">
            {activeRooms.length > 0 && (
              <div className="rs-active-rooms-strip">
                <span className="rs-active-lbl">Active:</span>
                {activeRooms.map(r => {
                  const hasPending = orders.some(o => o.room_number === r && o.status === "pending");
                  return <span key={r} className={`rs-room-chip ${hasPending ? "urgent" : ""}`}>#{r}</span>;
                })}
              </div>
            )}
            <button className={`rs-bell-btn ${newOrderRing ? "ringing" : ""}`}>
              <Bell size={18} />
              {stats.pending > 0 && <span className="rs-bell-badge">{stats.pending}</span>}
            </button>
            <button
              className={`rs-notify-toggle ${autoNotify ? "on" : ""}`}
              onClick={() => { setAutoNotify(v => !v); showToast(autoNotify ? "Auto-WhatsApp off" : "Auto-WhatsApp on"); }}
              title="Automatically WhatsApp the guest when you change an order's status"
            >
              <MessageSquare size={13} /> Auto-msg {autoNotify ? "On" : "Off"}
            </button>
            <button className="rs-refresh-btn" onClick={loadAll}><RefreshCw size={14} /></button>
            <button className="rs-kitchen-btn" onClick={() => navigate("/dashboard/roomservice/kitchen")}>
              <Monitor size={14} /> Kitchen
            </button>
            {tab === "orders" && <button className="rs-btn-primary" onClick={() => setShowNewOrder(true)}><Plus size={15} /> New Order</button>}
            {tab === "menu"   && <button className="rs-btn-primary" onClick={() => { setEditingItem({ name: "", category: "Main", price: 0, available: true, ingredients: [], unit: "serving" }); setEditMode("create"); }}><Plus size={15} /> Add Item</button>}
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="rs-stats-row">
          {[
            { label: "Today",      value: stats.total,      color: "rgba(242,235,217,0.3)", icon: Package },
            { label: "Pending",    value: stats.pending,    color: "#fbbf24", icon: Clock },
            { label: "Preparing",  value: stats.preparing,  color: "#6366f1", icon: ChefHat },
            { label: "On the way", value: stats.on_the_way, color: "#38bdf8", icon: Truck },
            { label: "Delivered",  value: stats.delivered,  color: "#34d399", icon: CheckCircle2 },
            { label: "Revenue",    value: `EGP ${stats.revenue.toLocaleString()}`, color: "#818cf8", icon: TrendingUp },
          ].map(s => (
            <div key={s.label} className="rs-stat-card">
              <s.icon size={16} style={{ color: s.color }} />
              <span className="rs-stat-val" style={{ color: s.color }}>{s.value}</span>
              <span className="rs-stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div className="rs-tabs">
          {tabDefs.map(t => (
            <button key={t.key} className={`rs-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <t.icon size={14} /> {t.label}
              {t.badge > 0 && <span className="rs-tab-badge">{t.badge}</span>}
            </button>
          ))}
        </div>

        {loading && <div className="rs-loading"><span className="rs-spinner" /> Loading…</div>}

        {/* ══ ORDERS ══ */}
        {!loading && tab === "orders" && (
          <>
            <div className="rs-scope-tabs">
              {[
                { key: "all",    label: "All" },
                { key: "rooms",  label: "🛏 Rooms" },
                { key: "tables", label: "🍽 Tables" },
              ].map(s => (
                <button key={s.key} className={`rs-scope-tab ${scope === s.key ? "active" : ""}`} onClick={() => setScope(s.key as any)}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="rs-filter-tabs">
              {[
                { key: "active",     label: "Active",     count: stats.pending + stats.preparing + stats.on_the_way },
                { key: "pending",    label: "Pending",    count: stats.pending },
                { key: "preparing",  label: "Preparing",  count: stats.preparing },
                { key: "on_the_way", label: "On the way", count: stats.on_the_way },
                { key: "delivered",  label: "Delivered",  count: stats.delivered },
                { key: "cancelled",  label: "Cancelled",  count: stats.cancelled },
                { key: "all",        label: "All",        count: stats.total },
              ].map(f => (
                <button key={f.key} className={`rs-filter-tab ${filterStatus === f.key ? "active" : ""}`} onClick={() => setFilterStatus(f.key)}>
                  {f.label}{f.count > 0 && <span className="rs-tab-count">{f.count}</span>}
                </button>
              ))}
            </div>

            {filteredOrders.length === 0
              ? <div className="rs-empty"><Package size={32} /><p>No {filterStatus} orders</p></div>
              : (
                <div className="rs-order-grid">
                  {filteredOrders.map(order => renderOrderCard(order))}
                </div>
              )
            }
          </>
        )}

        {/* ══ BOARD (delivery pipeline) ══ */}
        {!loading && tab === "board" && (
          <>
            <div className="rs-board-controls">
              <div className="rs-scope-tabs">
                {[{ key: "all", label: "All" }, { key: "rooms", label: "🛏 Rooms" }, { key: "tables", label: "🍽 Tables" }].map(s => (
                  <button key={s.key} className={`rs-scope-tab ${scope === s.key ? "active" : ""}`} onClick={() => setScope(s.key as any)}>{s.label}</button>
                ))}
              </div>
              <button className="rs-btn-ghost rs-stale-btn" onClick={cleanStale} title="Cancel pending orders older than 24h"><Trash size={13} /> Clear stale</button>
            </div>
            <div className="rs-board">
              {([
                { key: "pending",    label: "Pending" },
                { key: "preparing",  label: "Preparing" },
                { key: "on_the_way", label: "On the way" },
                { key: "delivered",  label: "Delivered" },
              ] as const).map(col => {
                const colOrders = orders.filter(o => inScope(o) && o.status === col.key);
                const cm = STATUS_META[col.key];
                return (
                  <div key={col.key} className="rs-board-col">
                    <div className="rs-board-col-head" style={{ borderColor: cm.color }}>
                      <span style={{ color: cm.color }}><cm.icon size={13} /> {col.label}</span>
                      <span className="rs-board-count">{colOrders.length}</span>
                    </div>
                    <div className="rs-board-col-body">
                      {colOrders.length === 0
                        ? <div className="rs-board-empty">—</div>
                        : colOrders.map(order => renderOrderCard(order))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ══ TABLES ══ */}
        {!loading && tab === "tables" && (
          <>
            {tableGroups.length === 0
              ? <div className="rs-empty"><UtensilsCrossed size={32} /><p>No open restaurant tables</p></div>
              : (
                <div className="rs-tables-grid">
                  {tableGroups.map(g => (
                    <div key={g.table} className={`rs-table-tile ${g.hasPending ? "pending" : "preparing"}`} onClick={() => { setScope("tables"); setFilterStatus("active"); setTab("orders"); }}>
                      <div className="rs-table-tile-head">
                        <span className="rs-table-tile-num"><UtensilsCrossed size={14} /> Table {g.table}</span>
                        <LiveTimer createdAt={new Date(g.oldest).toISOString()} status={g.hasPending ? "pending" : "preparing"} />
                      </div>
                      <div className="rs-table-tile-body">
                        {g.orders.flatMap(o => o.items).slice(0, 6).map((it, i) => (
                          <span key={i} className="rs-table-chip">{it.qty}× {it.name}</span>
                        ))}
                        {g.orders.flatMap(o => o.items).length > 6 && <span className="rs-table-chip more">+{g.orders.flatMap(o => o.items).length - 6} more</span>}
                      </div>
                      <div className="rs-table-tile-foot">
                        <span>{g.count} order{g.count !== 1 ? "s" : ""}</span>
                        <button className="rs-table-bill-btn" onClick={e => { e.stopPropagation(); openBill(g.table); }}>
                          Bill · EGP {g.total.toLocaleString()}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </>
        )}

        {/* ══ Z-REPORT ══ */}
        {tab === "zreport" && (
          <>
            <div className="rs-history-controls">
              <input type="date" className="rs-date-input" value={zdate} onChange={e => setZdate(e.target.value)} />
              {zdate !== todayISO() && <button className="rs-btn-ghost" onClick={() => setZdate(todayISO())}>Today</button>}
              <span className="rs-history-count">Daily sales report</span>
            </div>
            {!zreport
              ? <div className="rs-loading"><span className="rs-spinner" /> Loading…</div>
              : zreport.error
              ? <div className="rs-empty"><p>Couldn't load the report.</p></div>
              : (
                <div className="rs-zreport">
                  <div className="rs-z-kpis">
                    <div className="rs-z-kpi"><span className="rs-z-kpi-val" style={{ color: "#34d399" }}>EGP {Number(zreport.total).toLocaleString()}</span><span className="rs-z-kpi-lbl">Revenue (delivered)</span></div>
                    <div className="rs-z-kpi"><span className="rs-z-kpi-val" style={{ color: "#818cf8" }}>{zreport.orderCount}</span><span className="rs-z-kpi-lbl">Orders</span></div>
                    <div className="rs-z-kpi"><span className="rs-z-kpi-val" style={{ color: "#fbbf24" }}>EGP {Number(zreport.avgCheck).toLocaleString()}</span><span className="rs-z-kpi-lbl">Avg check</span></div>
                  </div>
                  <div className="rs-z-cols">
                    <div className="rs-z-card">
                      <div className="rs-z-title">By payment method</div>
                      {zreport.byMethod.length === 0 ? <div className="rs-z-empty">No delivered orders</div>
                        : zreport.byMethod.map((m: any) => (
                          <div key={m.method} className="rs-z-row">
                            <span className="rs-z-name">{m.method === "folio" ? "🏨 Room folio" : m.method === "unpaid" ? "⚠️ Unpaid" : m.method === "cash" ? "💵 Cash" : m.method === "card" ? "💳 Card" : m.method === "instapay" ? "📱 Instapay" : m.method}</span>
                            <span className="rs-z-amt">EGP {Number(m.revenue).toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                    <div className="rs-z-card">
                      <div className="rs-z-title">By category</div>
                      {zreport.byCategory.length === 0 ? <div className="rs-z-empty">—</div>
                        : zreport.byCategory.map((c: any) => (
                          <div key={c.category} className="rs-z-row">
                            <span className="rs-z-name">{c.category}</span>
                            <span className="rs-z-amt">EGP {Number(c.revenue).toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )
            }
          </>
        )}

        {/* ══ MENU ══ */}
        {!loading && tab === "menu" && (
          <>
            <div className="rs-menu-controls">
              <div className="rs-menu-search">
                <Search size={15} />
                <input placeholder="Search menu…" value={menuSearch} onChange={e => setMenuSearch(e.target.value)} />
                {menuSearch && <button onClick={() => setMenuSearch("")}><X size={13} /></button>}
              </div>
              <div className="rs-cat-tabs">
                {["All", ...CATEGORIES].map(c => (
                  <button key={c} className={`rs-cat-tab ${menuCat === c ? "active" : ""}`} onClick={() => setMenuCat(c)}>{c}</button>
                ))}
              </div>
            </div>
            {filteredMenu.length === 0
              ? <div className="rs-empty"><UtensilsCrossed size={32} /><p>No menu items</p></div>
              : (
                <div className="rs-menu-grid">
                  {filteredMenu.map(item => {
                    const nowAvail = isAvailableNow(item);
                    return (
                      <div key={item.id} className={`rs-menu-card ${!item.available ? "unavailable" : ""}`}>
                        {item.image_url && <img src={item.image_url} alt={item.name} className="rs-menu-img" />}
                        <div className="rs-menu-card-header">
                          <span className="rs-menu-cat-tag">{item.category}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {item.available_from && (
                              <span className={`rs-avail-tag ${nowAvail ? "avail" : "unavail"}`}>
                                {item.available_from}–{item.available_to}
                              </span>
                            )}
                            <button className="rs-toggle-btn" onClick={() => toggleAvailable(item)}>
                              {item.available ? <ToggleRight size={20} color="#34d399" /> : <ToggleLeft size={20} color="#475569" />}
                            </button>
                          </div>
                        </div>
                        <h3 className="rs-menu-name">{item.name}</h3>
                        {item.description && <p className="rs-menu-desc">{item.description}</p>}
                        <div className="rs-menu-price">EGP {Number(item.price).toLocaleString()}<span className="rs-menu-unit"> / {item.unit}</span></div>
                        {item.ingredients?.length > 0 && (
                          <div className="rs-menu-ingredients">
                            {item.ingredients.map((ing, i) => <span key={i} className="rs-ing-tag">{ing}</span>)}
                          </div>
                        )}
                        <div className="rs-menu-card-actions">
                          <button className="rs-action-btn edit" onClick={() => { setEditingItem({ ...item }); setEditMode("edit"); }}>
                            <Pencil size={13} /> Edit
                          </button>
                          <button className="rs-action-btn delete" onClick={() => deleteMenuItem(item.id)}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </>
        )}

        {/* ══ HISTORY ══ */}
        {tab === "history" && (
          <>
            <div className="rs-history-controls">
              <input type="date" className="rs-date-input" value={historyDate} onChange={e => setHistoryDate(e.target.value)} />
              {historyDate !== todayISO() && (
                <button className="rs-btn-ghost" onClick={() => setHistoryDate(todayISO())}>Today</button>
              )}
              <div className="rs-menu-search" style={{ flex: 1, maxWidth: 280 }}>
                <Search size={15} />
                <input placeholder="Search by room, guest, item…" value={historySearch} onChange={e => setHistorySearch(e.target.value)} />
                {historySearch && <button onClick={() => setHistorySearch("")}><X size={13} /></button>}
              </div>
              <span className="rs-history-count">{filteredHistory.length} orders</span>
            </div>

            {historyLoading
              ? <div className="rs-loading"><span className="rs-spinner" /> Loading…</div>
              : filteredHistory.length === 0
              ? <div className="rs-empty"><History size={32} /><p>No orders for this date</p></div>
              : (
                <div className="rs-history-list">
                  {filteredHistory.map(order => {
                    const sm = STATUS_META[order.status];
                    const Icon = sm.icon;
                    return (
                      <div key={order.id} className="rs-history-row">
                        <div className="rs-history-left">
                          <span className="rs-history-room"><BedDouble size={13} /> Room {order.room_number}</span>
                          {order.guest_name && <span className="rs-history-guest">{order.guest_name}</span>}
                          <span className="rs-history-time">{fmt(order.created_at)}</span>
                        </div>
                        <div className="rs-history-items">
                          {(order.items || []).map((it, i) => (
                            <span key={i} className="rs-history-item-chip">{it.qty}× {it.name}</span>
                          ))}
                        </div>
                        <div className="rs-history-right">
                          <span className="rs-history-total">EGP {Number(order.total).toLocaleString()}</span>
                          <span className="rs-order-status-badge" style={{ background: sm.bg, color: sm.color, fontSize: 10 }}>
                            <Icon size={10} /> {sm.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </>
        )}

        {/* ══ NEW ORDER ══ */}
        {tab === "new-order" && (
          <NewOrderFlow
            onOrderPlaced={loadAll}
            onViewOrders={() => setTab("orders")}
          />
        )}

        {/* ══ ANALYTICS ══ */}
        {tab === "analytics" && isManager && (
          <>
            {!analytics
              ? <div className="rs-loading"><span className="rs-spinner" /> Loading analytics…</div>
              : (
                <div className="rs-analytics">

                  {/* KPI row */}
                  <div className="rs-analytics-kpis">
                    <div className="rs-kpi-card">
                      <span className="rs-kpi-label">7-Day Revenue</span>
                      <span className="rs-kpi-value" style={{ color: "#34d399" }}>EGP {analytics.totalRevenue7d.toLocaleString()}</span>
                    </div>
                    <div className="rs-kpi-card">
                      <span className="rs-kpi-label">7-Day Orders</span>
                      <span className="rs-kpi-value" style={{ color: "#818cf8" }}>{analytics.totalOrders7d}</span>
                    </div>
                    <div className="rs-kpi-card">
                      <span className="rs-kpi-label">Avg Delivery Time</span>
                      <span className="rs-kpi-value" style={{ color: analytics.avgDeliveryTime && analytics.avgDeliveryTime > 25 ? "#f87171" : "#fbbf24" }}>
                        {analytics.avgDeliveryTime != null ? `${analytics.avgDeliveryTime} min` : "—"}
                      </span>
                    </div>
                    <div className="rs-kpi-card">
                      <span className="rs-kpi-label">Cancellation Rate</span>
                      <span className="rs-kpi-value" style={{ color: analytics.cancellationRate > 15 ? "#f87171" : "#64748b" }}>
                        {analytics.cancellationRate}%
                      </span>
                    </div>
                  </div>

                  {/* Daily Revenue */}
                  <div className="rs-analytics-card rs-analytics-full">
                    <div className="rs-analytics-title"><TrendingUp size={15} /> Daily Revenue — Last 7 Days</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={analytics.dailyRevenue} margin={{ left: 0, right: 10 }}>
                        <XAxis dataKey="date" tick={{ fill: "#475569", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Items */}
                  <div className="rs-analytics-card">
                    <div className="rs-analytics-title"><Star size={15} /> Most Ordered Items</div>
                    <div className="rs-top-items">
                      {analytics.topItems.map((item, i) => (
                        <div key={item.name} className="rs-top-item-row">
                          <span className="rs-top-rank">#{i + 1}</span>
                          <span className="rs-top-name">{item.name}</span>
                          <span className="rs-top-count">{item.count}×</span>
                          <span className="rs-top-rev">EGP {item.revenue.toLocaleString()}</span>
                          <div className="rs-top-bar-wrap">
                            <div className="rs-top-bar" style={{ width: `${(item.count / (analytics.topItems[0]?.count || 1)) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Revenue by Category */}
                  <div className="rs-analytics-card">
                    <div className="rs-analytics-title"><TrendingUp size={15} /> Revenue by Category</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.revenueByCategory} layout="vertical" margin={{ left: 0, right: 20 }}>
                        <XAxis type="number" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="category" width={80} tick={{ fill: "#64748b", fontSize: 12 }} />
                        <Tooltip formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                          {analytics.revenueByCategory.map((_, i) => (
                            <Cell key={i} fill={["#6366f1","#8b5cf6","#06b6d4","#34d399","#fbbf24","#f87171"][i % 6]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Peak Hours */}
                  <div className="rs-analytics-card rs-analytics-full">
                    <div className="rs-analytics-title"><Clock size={15} /> Peak Hours</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={analytics.peakHours.filter(h => h.count > 0 || (h.hour >= 6 && h.hour <= 23))} margin={{ left: 0, right: 10 }}>
                        <XAxis dataKey="hour" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={h => `${h}:00`} />
                        <YAxis tick={{ fill: "#475569", fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(v: any) => [v, "Orders"]} labelFormatter={h => `${h}:00`} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                </div>
              )
            }
          </>
        )}
      </div>

      {/* ══ New Order Modal ══ */}
      {showNewOrder && (
        <div className="rs-modal-overlay" onClick={() => setShowNewOrder(false)}>
          <div className="rs-modal" onClick={e => e.stopPropagation()}>
            <div className="rs-modal-header">
              <h2>New Room Service Order</h2>
              <button onClick={() => setShowNewOrder(false)}><X size={18} /></button>
            </div>
            <div className="rs-modal-body">
              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label>Room Number *</label>
                  <select value={newOrder.room_number} onChange={e => {
                    const val = e.target.value, info = inHouseMap[val];
                    setNewOrder(p => ({ ...p, room_number: val, guest_name: info?.guestName || p.guest_name, bookingId: info?.bookingId || "" }));
                  }}>
                    <option value="">— Select room —</option>
                    {Object.keys(inHouseMap).sort().map(r => (
                      <option key={r} value={r}>Room {r}{inHouseMap[r]?.guestName ? ` — ${inHouseMap[r].guestName}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div className="rs-form-group">
                  <label>Guest Name</label>
                  <input value={newOrder.guest_name} onChange={e => setNewOrder(p => ({ ...p, guest_name: e.target.value }))} placeholder="Auto-filled from room" />
                </div>
              </div>

              {/* Returning guest history */}
              {guestHistory.length > 0 && (
                <div className="rs-guest-history">
                  <div className="rs-guest-history-label"><History size={13} /> Previous orders from this room</div>
                  {guestHistory.map(prev => (
                    <div key={prev.id} className="rs-prev-order">
                      <div className="rs-prev-order-info">
                        <span>{fmt(prev.created_at)}</span>
                        <span>{prev.items?.length} items · EGP {prev.total}</span>
                      </div>
                      <div className="rs-prev-items">
                        {prev.items?.map((it, i) => <span key={i}>{it.qty}× {it.name}</span>)}
                      </div>
                      <button className="rs-btn-outline" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => {
                        setNewOrder(p => ({ ...p, items: [...p.items, ...prev.items] }));
                      }}>+ Reorder</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rs-form-group">
                <label>Notes</label>
                <input value={newOrder.notes} onChange={e => setNewOrder(p => ({ ...p, notes: e.target.value }))} placeholder="Special instructions…" />
              </div>
              <div className="rs-form-group">
                <label>Add Items</label>
                <div className="rs-order-item-input">
                  <select value={orderItemInput.name} onChange={e => {
                    const found = menu.find(m => m.name === e.target.value);
                    setOrderItemInput(p => ({ ...p, name: e.target.value, price: found?.price || p.price }));
                  }}>
                    <option value="">— Select menu item —</option>
                    {menu.filter(m => m.available).map(m => {
                      const avail = isAvailableNow(m);
                      return (
                        <option key={m.id} value={m.name} disabled={!avail}>
                          {m.name} — EGP {m.price}{!avail ? " (not available now)" : ""}
                        </option>
                      );
                    })}
                  </select>
                  <input type="number" min={1} value={orderItemInput.qty} onChange={e => setOrderItemInput(p => ({ ...p, qty: Number(e.target.value) }))} placeholder="Qty" style={{ width: 64 }} />
                  <input type="number" min={0} value={orderItemInput.price} onChange={e => setOrderItemInput(p => ({ ...p, price: Number(e.target.value) }))} placeholder="Price" style={{ width: 90 }} />
                  <button className="rs-btn-outline" onClick={() => {
                    if (!orderItemInput.name) return;
                    setNewOrder(p => ({ ...p, items: [...p.items, { ...orderItemInput }] }));
                    setOrderItemInput({ name: "", qty: 1, price: 0, notes: "" });
                  }}>+ Add</button>
                </div>
                {newOrder.items.map((it, i) => (
                  <div key={i} className="rs-order-item-row">
                    <span>{it.qty}× {it.name}</span>
                    <span>EGP {(it.price * it.qty).toLocaleString()}</span>
                    <button onClick={() => setNewOrder(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}><X size={12} /></button>
                  </div>
                ))}
                {newOrder.items.length > 0 && (
                  <div className="rs-order-total-row">Total: EGP {newOrder.items.reduce((s, i) => s + i.price * i.qty, 0).toLocaleString()}</div>
                )}
              </div>
            </div>
            <div className="rs-modal-footer">
              {orderError && <span className="rs-modal-error">{orderError}</span>}
              <button className="rs-btn-ghost" onClick={() => { setShowNewOrder(false); setOrderError(""); }}>Cancel</button>
              <button className="rs-btn-primary" onClick={submitNewOrder}>Place Order</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Edit Menu Item Modal ══ */}
      {editingItem && (
        <div className="rs-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="rs-modal rs-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="rs-modal-header">
              <h2>{editMode === "create" ? "Add Menu Item" : "Edit Menu Item"}</h2>
              <button onClick={() => setEditingItem(null)}><X size={18} /></button>
            </div>
            <div className="rs-modal-body">
              <div className="rs-form-row">
                <div className="rs-form-group" style={{ flex: 2 }}>
                  <label>Name *</label>
                  <input value={editingItem.name || ""} onChange={e => setEditingItem(p => ({ ...p!, name: e.target.value }))} placeholder="e.g. Club Sandwich" />
                </div>
                <div className="rs-form-group">
                  <label>Category</label>
                  <select value={editingItem.category || "Main"} onChange={e => setEditingItem(p => ({ ...p!, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label>Price (EGP)</label>
                  <input type="number" min={0} value={editingItem.price || 0} onChange={e => setEditingItem(p => ({ ...p!, price: Number(e.target.value) }))} />
                </div>
                <div className="rs-form-group">
                  <label>Unit</label>
                  <input value={editingItem.unit || "serving"} onChange={e => setEditingItem(p => ({ ...p!, unit: e.target.value }))} placeholder="serving / piece / cup" />
                </div>
                <div className="rs-form-group rs-form-toggle">
                  <label>Available</label>
                  <button className="rs-toggle-btn" onClick={() => setEditingItem(p => ({ ...p!, available: !p!.available }))}>
                    {editingItem.available ? <ToggleRight size={28} color="#34d399" /> : <ToggleLeft size={28} color="#475569" />}
                  </button>
                </div>
              </div>
              {/* Availability schedule */}
              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label>Available From (optional)</label>
                  <input type="time" value={editingItem.available_from || ""} onChange={e => setEditingItem(p => ({ ...p!, available_from: e.target.value || undefined }))} />
                </div>
                <div className="rs-form-group">
                  <label>Available To (optional)</label>
                  <input type="time" value={editingItem.available_to || ""} onChange={e => setEditingItem(p => ({ ...p!, available_to: e.target.value || undefined }))} />
                </div>
              </div>
              {/* Image upload */}
              <div className="rs-form-group">
                <label><Image size={11} style={{ display: "inline", marginRight: 4 }} />Image (optional)</label>
                <input type="file" accept="image/*" className="rs-file-input" onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setEditingItem(p => ({ ...p!, image_url: reader.result as string }));
                  reader.readAsDataURL(file);
                }} />
                {editingItem.image_url && (
                  <div className="rs-img-preview-wrap">
                    <img src={editingItem.image_url} alt="preview" className="rs-img-preview" />
                    <button className="rs-img-remove" onClick={() => setEditingItem(p => ({ ...p!, image_url: undefined }))}>
                      <X size={12} /> Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="rs-form-group">
                <label>Description</label>
                <textarea value={editingItem.description || ""} onChange={e => setEditingItem(p => ({ ...p!, description: e.target.value }))} placeholder="Brief description…" rows={2} />
              </div>
              <div className="rs-form-group">
                <label>Ingredients</label>
                <div className="rs-ing-input-row">
                  <input value={ingredientInput} onChange={e => setIngredientInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && ingredientInput.trim()) { setEditingItem(p => ({ ...p!, ingredients: [...(p!.ingredients || []), ingredientInput.trim()] })); setIngredientInput(""); }}}
                    placeholder="Type ingredient + Enter" />
                  <button className="rs-btn-outline" onClick={() => { if (!ingredientInput.trim()) return; setEditingItem(p => ({ ...p!, ingredients: [...(p!.ingredients || []), ingredientInput.trim()] })); setIngredientInput(""); }}>Add</button>
                </div>
                <div className="rs-ing-tags">
                  {(editingItem.ingredients || []).map((ing, i) => (
                    <span key={i} className="rs-ing-tag editable">
                      {ing}<button onClick={() => setEditingItem(p => ({ ...p!, ingredients: p!.ingredients!.filter((_, j) => j !== i) }))}><X size={10} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rs-modal-footer">
              {menuItemError && <span className="rs-modal-error">{menuItemError}</span>}
              <button className="rs-btn-ghost" onClick={() => { setEditingItem(null); setMenuItemError(""); }}>Cancel</button>
              <button className="rs-btn-primary" onClick={saveMenuItem}>{editMode === "create" ? "Add to Menu" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Edit Order Modal ══ */}
      {editOrder && (
        <div className="rs-modal-overlay" onClick={() => setEditOrder(null)}>
          <div className="rs-modal" onClick={e => e.stopPropagation()}>
            <div className="rs-modal-header">
              <h2>Edit Order — Room {editOrder.room_number}</h2>
              <button onClick={() => setEditOrder(null)}><X size={18} /></button>
            </div>
            <div className="rs-modal-body">
              <div className="rs-form-group">
                <label>Items</label>
                <div className="rs-order-item-input">
                  <select value={editItemInput.name} onChange={e => {
                    const found = menu.find(m => m.name === e.target.value);
                    setEditItemInput(p => ({ ...p, name: e.target.value, price: found?.price ?? p.price }));
                  }}>
                    <option value="">— Select menu item —</option>
                    {menu.filter(m => m.available).map(m => (
                      <option key={m.id} value={m.name}>{m.name} — EGP {m.price}</option>
                    ))}
                  </select>
                  <input type="number" min={1} value={editItemInput.qty} onChange={e => setEditItemInput(p => ({ ...p, qty: Number(e.target.value) }))} placeholder="Qty" style={{ width: 64 }} />
                  <input type="number" min={0} value={editItemInput.price} onChange={e => setEditItemInput(p => ({ ...p, price: Number(e.target.value) }))} placeholder="Price" style={{ width: 90 }} />
                  <button className="rs-btn-outline" onClick={() => {
                    if (!editItemInput.name) return;
                    setEditItems(p => [...p, { ...editItemInput }]);
                    setEditItemInput({ name: "", qty: 1, price: 0, notes: "" });
                  }}>+ Add</button>
                </div>
                {editItems.map((it, i) => (
                  <div key={i} className="rs-order-item-row">
                    <span>{it.qty}× {it.name}</span>
                    <input type="number" min={1} value={it.qty} onChange={e => setEditItems(p => p.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))} style={{ width: 56 }} />
                    <input type="number" min={0} value={it.price} onChange={e => setEditItems(p => p.map((x, j) => j === i ? { ...x, price: Number(e.target.value) } : x))} style={{ width: 80 }} />
                    <span>EGP {(it.price * it.qty).toLocaleString()}</span>
                    <button onClick={() => setEditItems(p => p.filter((_, j) => j !== i))}><X size={12} /></button>
                  </div>
                ))}
                {editItems.length > 0 && (
                  <div className="rs-order-total-row">Total: EGP {editItems.reduce((s, i) => s + i.price * i.qty, 0).toLocaleString()}</div>
                )}
              </div>
            </div>
            <div className="rs-modal-footer">
              {editError && <span className="rs-modal-error">{editError}</span>}
              <button className="rs-btn-ghost" onClick={() => setEditOrder(null)}>Cancel</button>
              <button className="rs-btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Table Bill / Settle Modal ══ */}
      {billTable && (() => {
        // Merge identical items across the table's open orders
        const merged: Record<string, { name: string; qty: number; price: number }> = {};
        billTable.orders.flatMap(o => o.items).forEach(it => {
          const k = `${it.name}@${it.price}`;
          if (!merged[k]) merged[k] = { name: it.name, qty: 0, price: it.price };
          merged[k].qty += Number(it.qty) || 1;
        });
        const lines = Object.values(merged);
        const total = lines.reduce((s, l) => s + l.price * l.qty, 0);
        return (
          <div className="rs-modal-overlay" onClick={() => setBillTable(null)}>
            <div className="rs-modal rs-bill-modal" onClick={e => e.stopPropagation()}>
              <div className="rs-modal-header">
                <h2>Table {billTable.table} — Bill</h2>
                <button onClick={() => setBillTable(null)}><X size={18} /></button>
              </div>
              <div className="rs-modal-body">
                {lines.length === 0
                  ? <div className="rs-empty"><p>No open items on this table.</p></div>
                  : (
                    <div className="rs-bill-lines">
                      {lines.map((l, i) => (
                        <div key={i} className="rs-bill-line">
                          <span className="rs-bill-qty">{l.qty}×</span>
                          <span className="rs-bill-name">{l.name}</span>
                          <span className="rs-bill-amt">EGP {(l.price * l.qty).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="rs-bill-total">
                        <span>Total</span>
                        <span>EGP {total.toLocaleString()}</span>
                      </div>
                    </div>
                  )
                }
                <div className="rs-form-group" style={{ marginTop: 16 }}>
                  <label>Payment method</label>
                  <div className="rs-bill-methods">
                    {(["cash", "card", "instapay"] as const).map(m => (
                      <button key={m} className={`rs-bill-method ${billMethod === m ? "active" : ""}`} onClick={() => setBillMethod(m)}>
                        {m === "cash" ? "💵 Cash" : m === "card" ? "💳 Card" : "📱 Instapay"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button className="rs-btn-ghost" onClick={() => setBillTable(null)}>Cancel</button>
                <button className="rs-btn-primary" disabled={settling || lines.length === 0} onClick={settleTable}>
                  {settling ? "Settling…" : `Settle ${billMethod} · Close Table`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ Per-room running tab ══ */}
      {roomOrders && (
        <div className="rs-modal-overlay" onClick={() => setRoomOrders(null)}>
          <div className="rs-modal rs-roomtab-modal" onClick={e => e.stopPropagation()}>
            <div className="rs-modal-header">
              <h2><BedDouble size={16} /> Room {roomOrders.room} — Orders</h2>
              <button onClick={() => setRoomOrders(null)}><X size={18} /></button>
            </div>
            <div className="rs-modal-body">
              {roomOrders.orders.length === 0
                ? <div className="rs-empty"><p>No orders for this room.</p></div>
                : (
                  <div className="rs-roomtab-list">
                    {roomOrders.orders.map(o => {
                      const sm = STATUS_META[o.status] || STATUS_META.pending;
                      return (
                        <div key={o.id} className="rs-roomtab-row">
                          <div className="rs-roomtab-top">
                            <span className="rs-order-status-badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                            <span className="rs-roomtab-time">{fmt(o.created_at)}</span>
                            <span className="rs-roomtab-total">EGP {Number(o.total).toLocaleString()}</span>
                          </div>
                          <div className="rs-roomtab-items">
                            {(o.items || []).map((it, i) => <span key={i} className="rs-table-chip">{it.qty}× {it.name}</span>)}
                          </div>
                        </div>
                      );
                    })}
                    <div className="rs-roomtab-foot">
                      <span>Running total (excl. cancelled)</span>
                      <strong>EGP {roomOrders.orders.filter(o => o.status !== "cancelled").reduce((s, o) => s + Number(o.total || 0), 0).toLocaleString()}</strong>
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      )}

      {/* ══ Accept ETA prompt ══ */}
      {acceptTarget && (
        <div className="rs-modal-overlay" onClick={() => setAcceptTarget(null)}>
          <div className="rs-modal rs-eta-modal" onClick={e => e.stopPropagation()}>
            <div className="rs-modal-header">
              <h2>Accept — Room {acceptTarget.room_number}</h2>
              <button onClick={() => setAcceptTarget(null)}><X size={18} /></button>
            </div>
            <div className="rs-modal-body">
              <p className="rs-eta-q">{acceptTarget.phone_number && autoNotify ? "Send the guest an ETA on WhatsApp?" : "Set an estimated time?"}</p>
              <div className="rs-eta-opts">
                {[15, 20, 30, 45].map(m => (
                  <button key={m} className="rs-eta-btn" onClick={() => confirmAccept(m)}>{m} min</button>
                ))}
                <button className="rs-eta-btn ghost" onClick={() => confirmAccept(null)}>No ETA</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Order details drawer ══ */}
      {detailOrder && (() => {
        const o = detailOrder;
        const sm = STATUS_META[o.status] || STATUS_META.pending;
        const guest = o.booking_guest || o.guest_name;
        return (
          <div className="rs-drawer-overlay" onClick={() => setDetailOrder(null)}>
            <div className="rs-drawer" onClick={e => e.stopPropagation()}>
              <div className="rs-drawer-head">
                <div>
                  <h2>{o.is_room === false ? "Table" : "Room"} {o.room_number}</h2>
                  <span className="rs-order-status-badge" style={{ background: sm.bg, color: sm.color }}><sm.icon size={11} /> {sm.label}</span>
                </div>
                <button onClick={() => setDetailOrder(null)}><X size={18} /></button>
              </div>
              <div className="rs-drawer-body">
                <div className="rs-drawer-meta">
                  {guest && <div><User size={13} /> {guest}</div>}
                  {o.phone_number && <div>💬 {o.phone_number}</div>}
                  {o.is_room !== false && <div>{o.billed ? "✅ On room folio" : "⚠️ No active booking"}</div>}
                </div>
                <div className="rs-drawer-timeline">
                  <div className="rs-tl-item"><span className="rs-tl-dot" /> Ordered · {fmt(o.created_at)} <span className="rs-tl-ago">({elapsed(o.created_at)})</span></div>
                  <div className="rs-tl-item"><span className="rs-tl-dot" style={{ background: sm.color }} /> Now · {sm.label}</div>
                </div>
                <div className="rs-drawer-items">
                  {(o.items || []).map((it, i) => {
                    const m = menuMatch(it.name);
                    return (
                      <div key={i} className="rs-drawer-item">
                        <span>{it.qty}× {it.name}{!m && <AlertTriangle size={11} className="rs-item-flag" />}</span>
                        <span>EGP {(it.price * it.qty).toLocaleString()}</span>
                      </div>
                    );
                  })}
                  {o.notes && <div className="rs-drawer-notes">📝 {o.notes}</div>}
                  <div className="rs-drawer-total"><span>Total</span><span>EGP {Number(o.total).toLocaleString()}</span></div>
                </div>
                <div className="rs-drawer-actions">
                  <button className="rs-btn-outline" onClick={() => { setDetailOrder(null); openEdit(o); }}><Pencil size={13} /> Edit</button>
                  {!!o.phone_number && (o.status === "preparing" || o.status === "on_the_way") && (
                    <button className="rs-btn-outline" onClick={() => notifyOrder(o, o.status)}><MessageSquare size={13} /> Message guest</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ Toast ══ */}
      {toast && <div className={`rs-toast ${toast.kind}`}>{toast.text}</div>}
    </DashboardLayout>
  );
}
